import { encodeName } from './decodeName';
import type { HierarchicalNode } from '../types';
import type { AddClusteringNodeData } from '@/app/KeywordResearch/types/clusteringApiTypes';

export type TreePath = string[];

function cloneNode(node: HierarchicalNode): HierarchicalNode {
  return {
    ...node,
    children: node.children?.map(cloneNode),
    intent: node.intent ? [...node.intent] : node.intent,
    purpose: node.purpose ? [...node.purpose] : node.purpose
  };
}

/** Walk tree by exact `name` segments (raw names as stored in JSON). */
export function findNodeByPath(
  root: HierarchicalNode | null | undefined,
  path: TreePath
): HierarchicalNode | null {
  if (!root || !path.length) return null;
  if (root.name !== path[0]) return null;
  let current: HierarchicalNode = root;
  for (let i = 1; i < path.length; i += 1) {
    const next = (current.children ?? []).find(child => child.name === path[i]);
    if (!next) return null;
    current = next;
  }
  return current;
}

function findParentRef(
  root: HierarchicalNode,
  path: TreePath
): { parent: HierarchicalNode; index: number } | null {
  if (path.length < 2) return null;
  const parent = findNodeByPath(root, path.slice(0, -1));
  if (!parent?.children) return null;
  const index = parent.children.findIndex(
    child => child.name === path[path.length - 1]
  );
  if (index < 0) return null;
  return { parent, index };
}

export type NewNodePayload = {
  name: string;
};

/** Map add_node API payload into a hierarchical leaf (keeps encoded `name`). */
export function hierarchicalNodeFromApi(
  data: AddClusteringNodeData
): HierarchicalNode {
  return {
    name: data.name,
    search_volume_miztols: data.search_volume_miztols ?? null,
    difficulty: data.difficulty ?? null,
    len: data.len ?? null,
    intent: data.intent ?? null,
    purpose: data.purpose ?? null
  };
}

export function createHierarchicalNode(
  payload: NewNodePayload
): HierarchicalNode {
  return {
    name: payload.name.trim(),
    search_volume_miztols: null,
    difficulty: null,
    len: null,
    intent: null,
    purpose: null
  };
}

export function addChildNode(
  root: HierarchicalNode,
  parentPath: TreePath,
  child: HierarchicalNode,
  position: number | 'end' = 'end'
): HierarchicalNode {
  const next = cloneNode(root);
  const parent = findNodeByPath(next, parentPath);
  if (!parent) {
    throw new Error('والد نود پیدا نشد');
  }
  if (!parent.children) parent.children = [];
  if (parent.children.some(item => item.name === child.name)) {
    throw new Error('نودی با این نام در همین سطح وجود دارد');
  }
  const inserted = cloneNode(child);
  if (position === 'end' || position >= parent.children.length) {
    parent.children.push(inserted);
  } else {
    parent.children.splice(Math.max(0, position), 0, inserted);
  }
  return next;
}

export type NodePatch = {
  name: string;
};

export function updateNodeAtPath(
  root: HierarchicalNode,
  path: TreePath,
  patch: NodePatch
): HierarchicalNode {
  const next = cloneNode(root);
  const node = findNodeByPath(next, path);
  if (!node) {
    throw new Error('نود برای ویرایش پیدا نشد');
  }
  const trimmed = patch.name.trim();
  if (!trimmed) throw new Error('نام نود نمی‌تواند خالی باشد');
  // Backend stores encoded names; keep local tree in sync for future path calls.
  const storedName = encodeName(trimmed);
  if (path.length > 1) {
    const parent = findNodeByPath(next, path.slice(0, -1));
    const conflict = parent?.children?.some(
      child => child.name === storedName && child.name !== node.name
    );
    if (conflict) throw new Error('نودی با این نام در همین سطح وجود دارد');
  }
  node.name = storedName;
  return next;
}

export function deleteNodeAtPath(
  root: HierarchicalNode,
  path: TreePath
): HierarchicalNode {
  if (path.length <= 1) {
    throw new Error('نود ریشه قابل حذف نیست');
  }
  const next = cloneNode(root);
  const ref = findParentRef(next, path);
  if (!ref) throw new Error('نود برای حذف پیدا نشد');
  ref.parent.children!.splice(ref.index, 1);
  return next;
}

/** Raw-name path from root → node (stable identity for APIs). */
export function getRawPathFromModel(
  nodesById: Map<
    string,
    { id: string; parentId: string | null; rawName: string }
  >,
  nodeId: string
): TreePath {
  const path: string[] = [];
  let current = nodesById.get(nodeId);
  while (current) {
    path.unshift(current.rawName);
    current = current.parentId
      ? nodesById.get(current.parentId)
      : undefined;
  }
  return path;
}

/**
 * Path for API bodies:
 * - keep stored names exactly as-is (API-encoded or user plain)
 * - omit root label («اصلی» / main) — backend paths start from first real cluster/keyword
 */
export function toApiNamePath(path: TreePath): TreePath {
  if (!path.length) return [];
  const [first, ...rest] = path;
  const rootLabel = String(first);
  const isRoot =
    rootLabel === 'اصلی' ||
    rootLabel === 'main' ||
    rootLabel === encodeURIComponent('اصلی');
  return isRoot ? [...rest] : [...path];
}
